// import React, { useState, useEffect, useCallback, useMemo } from "react";
// import {
//   Table,
//   TableHeader,
//   TableColumn,
//   TableBody,
//   TableRow,
//   TableCell,
//   User,
//   Chip,
//   Spinner,
//   Button,
//   Pagination,
// } from "@nextui-org/react";
// import axios from "axios";
// import { toast } from "react-toastify";

// const rowsPerPage = 6;

// const fetchBatchSize = 100;

// export default function FlaggedComment() {
//   /* ─── STATE ─────────────────────────────── */
//   const [reports, setReports] = useState([]);
//   const [loading, setLoading] = useState(false);

//   const [page, setPage] = useState(1);

//   const token = localStorage.getItem("accessToken");

//   const fetchAllReports = useCallback(async () => {
//     if (!token) return toast.error("Auth is missing");

//     setLoading(true);
//     try {
//       let pg = 1;
//       let done = false;
//       const all = [];

//       while (!done) {
//         const { data } = await axios.get(
//           `${process.env.REACT_APP_SERVER_URL}/admin/reports?page=${pg}&pageSize=${fetchBatchSize}`,
//           { headers: { authorization: `Bearer ${token}` } }
//         );

//         console.log(`page ${pg}`, data);

//         const list =
//           data.details?.reports ?? data.data?.reports ?? data.data ?? [];

//         all.push(...list);

//         const meta =
//           data.details?.index ?? data.index ?? data.data?.index ?? {};

//         const lastPage = meta.totalPages ?? pg;

//         done = pg >= lastPage || list.length === 0;
//         pg += 1;
//       }

//       setReports(all);
//       setPage(1);
//     } catch (err) {
//       console.error(err);
//       toast.error(err.response?.data?.message ?? "Unable to load reports");
//     } finally {
//       setLoading(false);
//     }
//   }, [token]);

//   useEffect(() => {
//     fetchAllReports();
//   }, [fetchAllReports]);

//   /* ─── FRONT-END PAGINATION MATH ─────────── */
//   const totalPages = Math.max(1, Math.ceil(reports.length / rowsPerPage));

//   const currentRows = useMemo(() => {
//     const start = (page - 1) * rowsPerPage;
//     return reports.slice(start, start + rowsPerPage);
//   }, [reports, page]);

//   useEffect(() => {
//     if (page > totalPages) setPage(totalPages);
//   }, [totalPages, page]);

//   /* ─── DELETE HANDLER ────────────────────── */
//   const handleDelete = async (reportId) => {
//     if (!token) return toast.error("Auth token missing hai");
//     try {
//       await axios.delete(
//         `${process.env.REACT_APP_SERVER_URL}/admin/deletereport/${reportId}`,
//         { headers: { authorization: `Bearer ${token}` } }
//       );

//       setReports((prev) => prev.filter((r) => r.id !== reportId));
//       toast.success("Report deleted");
//     } catch (err) {
//       console.error(err);
//       toast.error(err.response?.data?.message ?? "Unable to delete report");
//     }
//   };

//   /* ─── UI ────────────────────────────────── */
//   if (loading) {
//     return (
//       <div className="flex justify-center items-center h-56">
//         <Spinner label="Loading flagged comments…" />
//       </div>
//     );
//   }

//   return (
//     <div className="mt-6 flex flex-col gap-4">
//       <Table aria-label="Flagged comments table" shadow="sm" fullWidth>
//         <TableHeader>
//           <TableColumn>Reporter</TableColumn>
//           <TableColumn>Role</TableColumn>
//           <TableColumn>Reason</TableColumn>
//           <TableColumn>Date / Time</TableColumn>
//           <TableColumn>Action</TableColumn>
//         </TableHeader>

//         <TableBody
//           items={currentRows}
//           emptyContent="No flagged comments."
//         >
//           {(r) => (
//             <TableRow key={r.id}>
//               <TableCell>
//                 <User
//                   name={`${r.user?.first_name ?? ""} ${
//                     r.user?.last_name ?? ""
//                   }`}
//                   description={`ID: ${r.user?.id ?? ""}`}
//                   avatarProps={{
//                     src: r.user?.profile_id ?? undefined,
//                   }}
//                 />
//               </TableCell>
//               <TableCell>{r.reported_by_role ?? r.reportedByRole}</TableCell>
//               <TableCell>
//                 <Chip color="danger" size="sm" variant="flat">
//                   {r.reason}
//                 </Chip>
//               </TableCell>
//               <TableCell>
//                 {new Date(r.createdAt ?? r.created_at).toLocaleString()}
//               </TableCell>
//               <TableCell>
//                 <Button
//                   size="sm"
//                   color="danger"
//                   variant="flat"
//                   onClick={() => handleDelete(r.id)}
//                 >
//                   Delete
//                 </Button>
//               </TableCell>
//             </TableRow>
//           )}
//         </TableBody>
//       </Table>

//       {/* pagination controls */}
//       <div className="flex justify-center">
//         <Pagination
//           page={page}
//           total={totalPages}
//           onChange={setPage}
//           showControls
//         />
//       </div>
//     </div>
//   );
// }
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  User,
  Chip,
  Spinner,
  Button,
  Pagination,
} from "@nextui-org/react";
import axios from "axios";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { refreshIfNeeded } from "../../store/actions/refreshIfNeeded";

const rowsPerPage = 6;
const fetchBatchSize = 100;

export default function FlaggedComment() {
  /* ─── STATE ─────────────────────────────── */
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [deletingId, setDeletingId] = useState(null);

  const dispatch = useDispatch();

  // ✅ Always get a fresh token just before making any API call
  const ensureAuth = useCallback(async () => {
    await dispatch(refreshIfNeeded());
    const fresh = localStorage.getItem("accessToken");
    return { headers: { Authorization: `Bearer ${fresh}` } };
  }, [dispatch]);

  const fetchAllReports = useCallback(async () => {
    setLoading(true);
    try {
      let pg = 1;
      let done = false;
      const all = [];

      // per-page ensureAuth → safe if many pages or near-expiry tokens
      while (!done) {
        const auth = await ensureAuth();
        const { data } = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/admin/reports`,
          { ...auth, params: { page: pg, pageSize: fetchBatchSize } }
        );

        const list =
          data?.details?.reports ?? data?.data?.reports ?? data?.data ?? [];

        all.push(...list);

        const meta =
          data?.details?.index ?? data?.index ?? data?.data?.index ?? {};
        const lastPage = meta?.totalPages ?? pg;

        done = pg >= lastPage || list.length === 0;
        pg += 1;
      }

      setReports(all);
      setPage(1);
    } catch (err) {
      console.error(err);
      const code = err?.response?.status;
      if (code === 401) {
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error(err?.response?.data?.message ?? "Unable to load reports");
      }
    } finally {
      setLoading(false);
    }
  }, [ensureAuth]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (alive) await fetchAllReports();
    })();
    return () => {
      alive = false;
    };
  }, [fetchAllReports]);

  /* ─── FRONT-END PAGINATION MATH ─────────── */
  const totalPages = Math.max(1, Math.ceil(reports.length / rowsPerPage));

  const currentRows = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return reports.slice(start, start + rowsPerPage);
  }, [reports, page]);

  // If page goes out of range after data changes, bring it back
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  /* ─── DELETE HANDLER ────────────────────── */
  const handleDelete = async (reportId) => {
    try {
      setDeletingId(reportId);
      const auth = await ensureAuth();
      await axios.delete(
        `${process.env.REACT_APP_SERVER_URL}/admin/deletereport/${reportId}`,
        auth
      );
      setReports((prev) => prev.filter((r) => r.id !== reportId));
      toast.success("Report deleted");
    } catch (err) {
      console.error(err);
      const code = err?.response?.status;
      if (code === 401) {
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error(err?.response?.data?.message ?? "Unable to delete report");
      }
    } finally {
      setDeletingId(null);
    }
  };

  /* ─── UI ────────────────────────────────── */
  if (loading) {
    return (
      <div className="flex justify-center items-center h-56">
        <Spinner label="Loading flagged comments…" />
      </div>
    );
  }

  return (
    <div className="mt-6 flex flex-col gap-4">
      <Table aria-label="Flagged comments table" shadow="sm" fullWidth>
        <TableHeader>
          <TableColumn>Reporter</TableColumn>
          <TableColumn>Role</TableColumn>
          <TableColumn>Reason</TableColumn>
          <TableColumn>Date / Time</TableColumn>
          <TableColumn>Action</TableColumn>
        </TableHeader>

        <TableBody items={currentRows} emptyContent="No flagged comments.">
          {(r) => {
            const reporter = r?.user || r?.User || {};
            const created = r?.createdAt || r?.created_at;

            return (
              <TableRow key={r.id}>
                <TableCell>
                  <User
                    name={`${reporter.first_name ?? ""} ${
                      reporter.last_name ?? ""
                    }`}
                    description={`ID: ${reporter.id ?? ""}`}
                    avatarProps={{
                      src: reporter.profile_id ?? undefined,
                    }}
                  />
                </TableCell>
                <TableCell>
                  {r?.reported_by_role ?? r?.reportedByRole}
                </TableCell>
                <TableCell>
                  <Chip color="danger" size="sm" variant="flat">
                    {r?.reason}
                  </Chip>
                </TableCell>
                <TableCell>
                  {created ? new Date(created).toLocaleString() : "-"}
                </TableCell>
                <TableCell>
                  <Button
                    size="sm"
                    color="danger"
                    variant="flat"
                    isDisabled={deletingId === r.id}
                    isLoading={deletingId === r.id}
                    onClick={() => handleDelete(r.id)}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            );
          }}
        </TableBody>
      </Table>

      {/* pagination controls */}
      <div className="flex justify-center">
        <Pagination
          page={page}
          total={totalPages}
          onChange={setPage}
          showControls
        />
      </div>
    </div>
  );
}
